import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { IconDefinition, faCircle } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { buffer, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { shortcutStringBuilderFromKeyboardEvent } from 'src/app/helper/shortcutStringBuilder';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import { ShortcutTypeDirectory } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeDirectory';
import { ChangedHotkeyCombo } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.actions';
import { DirectorySettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-settings/directory-settings.state';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';
import { ShortcutBehaviourState } from 'src/app/store/samwin/shortcut-behaviour/shortcut-behaviour.state';

@Component({
   selector: 'samweb-directory-hotkey-setting',
   templateUrl: './directory-hotkey-setting.component.html',
   styleUrls: ['./directory-hotkey-setting.component.scss'],
})
export class DirectoryHotkeySettingComponent implements OnInit, OnDestroy {
   @Input()
   public brickInstanceId: string;

   private destroy$ = new Subject();

   @Input()
   public disabled: boolean;

   @Input()
   public shortcutTyp: ShortcutTypeDirectory;

   @Input()
   public searchValue$: Observable<string>;

   public keyEvent$ = new Subject<KeyboardEvent>();

   public hotkeyCombo$: Observable<string>;

   public recordIcon: IconDefinition = faCircle;

   public clearFilterIcon: IconDefinition = WellKnownIconsEnum.CLEAR.iconSmall;

   public isRecording = false;

   public eventShortcutString: string;

   public column: DirectoryColumn;

   @Select(ShortcutBehaviourState.getHotkeysWithValue)
   public shortCutList$: Observable<string[]>;
   public isDuplicateDialogOpen$: Observable<boolean>;

   constructor(private store: Store) {}

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   ngOnInit() {
      this.column = this.store.selectSnapshot(DirectoryStructureState.getDirectoryColumnFromKey)(this.shortcutTyp.columnName);
      this.hotkeyCombo$ = this.store.select(DirectorySettingsState.getHotkeyForAction).pipe(
         map((getHotkeySettingForHotkeyType) => getHotkeySettingForHotkeyType(this.brickInstanceId, this.shortcutTyp.enumName)),
         takeUntil(this.destroy$)
      );
      this.shortCutList$
         .pipe(
            switchMap(() =>
               this.keyEvent$.pipe(
                  buffer(this.keyEvent$.pipe(debounceTime(250))),
                  map((eventDatas: KeyboardEvent[]) =>
                     _(
                        _(eventDatas)
                           .map((event) => shortcutStringBuilderFromKeyboardEvent(event))
                           .sortBy((customKeycode) => customKeycode.length)
                           .value()
                     ).last()
                  ),
                  filter((eventShortcutString: string) => eventShortcutString?.length > 0),
                  tap((eventShortcutString: string) => {
                     console.debug('Events: ', eventShortcutString);
                     if (this.eventShortcutString === eventShortcutString) {
                        return;
                     }
                     this.eventShortcutString = eventShortcutString;
                     // TODO: add duplicate shortcut handling
                     this.store.dispatch(new ChangedHotkeyCombo(this.brickInstanceId, this.shortcutTyp, this.eventShortcutString));
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   @HostListener('keydown', ['$event'])
   private onKeyDown(eventData: KeyboardEvent) {
      if (!this.isRecording) {
         return;
      }
      this.keyEvent$.next(eventData);
      eventData.stopPropagation();
      eventData.preventDefault();
   }

   @HostListener('keyup', ['$event'])
   private onKeyUp(eventData: KeyboardEvent) {
      // PrintScreen and Meta key are only triggered on keyup
      if (!this.isRecording || eventData.code !== 'PrintScreen' || !eventData.metaKey) {
         return;
      }
      this.keyEvent$.next(eventData);
      eventData.stopPropagation();
      eventData.preventDefault();
   }

   clearShortcut() {
      const currentShortcutCombo: string = this.store.selectSnapshot(DirectorySettingsState.getHotkeyForAction)(
         this.brickInstanceId,
         this.shortcutTyp.enumName
      );
      if (currentShortcutCombo === '') {
         // reset to default
         this.store.dispatch(new ChangedHotkeyCombo(this.brickInstanceId, this.shortcutTyp, null));
      } else {
         // remove shortcut
         this.store.dispatch(new ChangedHotkeyCombo(this.brickInstanceId, this.shortcutTyp, ''));
      }
   }

   comboChanged(value: any) {
      this.store.dispatch(new ChangedHotkeyCombo(this.brickInstanceId, this.shortcutTyp, value));
   }

   stopRecording() {
      this.isRecording = false;
   }

   startRecording() {
      this.isRecording = true;
   }
}
